import { ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { OptionField } from 'components/Field/OptionField';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ICONS } from 'components/Icon';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import { noop, sortBy } from 'lodash';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  doReportingGetReportListData,
  doReportingSelectReport,
  doReportingMergeDraftFilterValues,
  doReportingResetDraftFilterValues,
  doReportingClosePage,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  selectRptReportBasics,
  selectRptReportDraftFilterIsDirty,
  selectRptReportDraftFilterValues,
  selectRptReportFilterOptions,
  selectRptReportLoaded,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';

type Report = ReportTypes[ReportEnum.assets];

export const Filter: React.FC<{
  reportKey: string;
}> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const basics = useSelector((state) =>
    selectRptReportBasics(state, reportKey),
  )!;

  const isLoaded = useSelector((state) =>
    selectRptReportLoaded(state, reportKey),
  )!;

  const filterOptions = useSelector((state) =>
    selectRptReportFilterOptions(state, reportKey),
  ) as Report['filterOptions'];

  const draftFilterValues = useSelector((state) =>
    selectRptReportDraftFilterValues(state, reportKey),
  ) as Report['filterValues'];

  const draftFilterIsDirty = useSelector((state) =>
    selectRptReportDraftFilterIsDirty(state, reportKey),
  );

  const organisationOptions = React.useMemo(
    () =>
      sortBy(
        (filterOptions.organisations || []).map((organisation) => ({
          name: organisation.name,
          internalValue: organisation.value,
        })),
        ['name'],
      ),
    [filterOptions.organisations],
  );

  const showOutOf = React.useCallback(
    (total: number, chosen: number) =>
      chosen ? `${chosen} / ${total}` : total,
    [],
  );

  const handleSetFilterValue = React.useCallback(
    <
      T extends keyof Report['filterValues'],
      V extends Report['filterValues'][T],
    >(
      name: T,
      value: V,
    ) =>
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: { [name]: value },
          loadData: false,
        }),
      ),
    [dispatch, reportKey],
  );

  const handleSetOrganisationIds = React.useCallback(
    (newOrganisationIds: string[]) =>
      basics?.id
        ? dispatch(
            doReportingSelectReport({
              id: basics.id,
              key: reportKey,
              filter: {
                ...draftFilterValues,
                organisationIds: newOrganisationIds,
              },
              loadData: isLoaded,
            }),
          )
        : noop,
    [basics.id, dispatch, draftFilterValues, isLoaded, reportKey],
  );

  const handleApplyFilter = React.useCallback(
    () =>
      dispatch(
        doReportingGetReportListData({
          key: reportKey,
          nextPage: false,
          closeModals: true,
          overlayLoader: true,
        }),
      ),
    [dispatch, reportKey],
  );

  const handleClose = React.useCallback(() => {
    const doClose = () => {
      if (isLoaded) {
        dispatch(doReportingResetDraftFilterValues({ key: reportKey }));
      } else {
        dispatch(doReportingClosePage({ key: reportKey }));
      }
      dispatch(NAVIGATE_MODAL_BACK());
    };
    if (draftFilterIsDirty) {
      confirmUnsaved({
        onSave: handleApplyFilter,
        onDiscard: doClose,
        text: smartText("You haven't applied your changes"),
        onDiscardText: 'Discard Filter',
        onSaveText: 'Apply Filter',
      });
      return;
    }
    doClose();
  }, [
    confirmUnsaved,
    dispatch,
    draftFilterIsDirty,
    isLoaded,
    reportKey,
    handleApplyFilter,
  ]);

  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={basics.name}
          saveButtonText="Load"
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={
            !isLoaded || draftFilterIsDirty ? handleApplyFilter : handleClose
          }
          saveButtonTheme={
            !isLoaded || draftFilterIsDirty
              ? ButtonTheme.primary
              : ButtonTheme.themeless
          }
        />
      }
    >
      <ViewWithSeparators separatorMargin={theme.paddingVertical / 2}>
        {organisationOptions.length > 1 && (
          <Card margin={false}>
            <OptionField
              label={`Organisations (${showOutOf(
                organisationOptions.length,
                draftFilterValues.organisationIds.length,
              )})`}
              modalLabel="Organisations"
              labelAlignTop
              value={draftFilterValues.organisationIds}
              valueNumberOfLines={2}
              onChange={handleSetOrganisationIds}
              options={organisationOptions}
              noValueOption={{ name: 'All Organisations' }}
              showNoValueOption
              multiSelect
              showFieldTypeIcon={false}
              modalSize="small"
              modalSearchable
              modalEnableSelectAll
              showLabelInCloseHeader
              forceModal
              modalCloseOnDone={false}
              expandedInlineFirst
              expandedInlineLast
              showBadge={!!draftFilterValues.organisationIds.length}
            />
          </Card>
        )}
        <ViewWithSeparators
          separatorMargin={theme.paddingVertical / 4}
          separatorColor="transparent"
        >
          <Card margin={false}>
            <TextAreaField
              iconLeft={ICONS.MagnifyingGlassLight}
              value={draftFilterValues.search}
              name="search"
              onChangeWithName={handleSetFilterValue}
              placeholder="Search"
              showFieldTypeIcon={false}
              clearable
              onEsc={handleClose}
            />
          </Card>
        </ViewWithSeparators>
      </ViewWithSeparators>
    </PageScrollView>
  );
};
